import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Text-to-speech for flashcard audio.
 *
 * Audio is synthesized in the browser rather than shipped as files, which is
 * what lets a new language be added by writing a JSON file and nothing else —
 * no recording, no bucket, no build step. The trade is that voice availability
 * depends on the learner's operating system.
 *
 * That trade drives the one rule this hook enforces: **it will not speak
 * without a voice for the requested language.** If a machine has no Spanish
 * voice installed, `speechSynthesis` will happily read "el amigo" in a US
 * English voice, and the learner drilling listening comprehension would be
 * memorizing a pronunciation that does not exist. Saying nothing and reporting
 * `voiceAvailable: false` is worse UX and better teaching, so the card hides
 * the audio option instead.
 *
 * Everything the rest of the app knows about audio goes through this hook's
 * return value. Swapping in pre-generated Polly clips later means changing this
 * file and nothing that imports it.
 */

export interface Speech {
  /** The browser exposes the speech synthesis API at all. */
  supported: boolean;
  /** ...and has a voice that actually speaks the requested language. */
  voiceAvailable: boolean;
  /** The chosen voice's name, for the settings panel. */
  voiceName: string | null;
  speaking: boolean;
  /** Speaks `text`, cancelling anything already in flight. A no-op if no voice. */
  speak: (text: string, rate?: number) => void;
  cancel: () => void;
}

/**
 * Picks the best voice for a BCP 47 tag.
 *
 * Preference order: an exact tag match ("es-ES"), then any voice for the same
 * language ("es-MX" for "es-ES" — a different accent, but genuinely Spanish),
 * then nothing. A regional accent is a small cost; the wrong language is not a
 * cost the learner can detect, which is why it is refused rather than allowed.
 */
function pickVoice(voices: SpeechSynthesisVoice[], speechTag: string): SpeechSynthesisVoice | null {
  if (!speechTag) return null;
  const wanted = speechTag.toLowerCase();
  const base = wanted.split('-')[0];

  const exact = voices.find((v) => v.lang.toLowerCase() === wanted);
  if (exact) return exact;

  const normalized = wanted.replace('-', '_');
  const exactUnderscore = voices.find((v) => v.lang.toLowerCase() === normalized);
  if (exactUnderscore) return exactUnderscore;

  // Same language, different region. Prefer a local (offline) voice — remote
  // ones are better but stall on a slow connection, and a flashcard drill is
  // paced in hundreds of milliseconds.
  const sameLanguage = voices.filter((v) => v.lang.toLowerCase().replace('_', '-').split('-')[0] === base);
  return sameLanguage.find((v) => v.localService) ?? sameLanguage[0] ?? null;
}

export function useSpeech(speechTag: string): Speech {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => {
    if (!supported) return;

    // getVoices() is empty on first call in every Chromium browser; the list
    // arrives later on the voiceschanged event. Read it now for Safari and
    // Firefox, and again when the event fires.
    const read = () => setVoices(window.speechSynthesis.getVoices());
    read();
    window.speechSynthesis.addEventListener('voiceschanged', read);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', read);
  }, [supported]);

  // Anything still being spoken when the drill is left should stop immediately,
  // rather than following the learner onto the next page.
  useEffect(() => {
    if (!supported) return;
    return () => window.speechSynthesis.cancel();
  }, [supported]);

  const voice = useMemo(() => pickVoice(voices, speechTag), [voices, speechTag]);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
  voiceRef.current = voice;

  const cancel = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback(
    (text: string, rate = 0.9) => {
      const chosen = voiceRef.current;
      if (!supported || !chosen || !text.trim()) return;

      // Without this, rapid clicks queue up and the card plays four times over.
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = chosen;
      // Setting lang as well as voice matters: some engines consult it to pick
      // the pronunciation rules even once a voice is chosen.
      utterance.lang = chosen.lang;
      utterance.rate = Math.min(Math.max(rate, 0.5), 1.5);
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);

      window.speechSynthesis.speak(utterance);
    },
    [supported],
  );

  return {
    supported,
    voiceAvailable: Boolean(voice),
    voiceName: voice?.name ?? null,
    speaking,
    speak,
    cancel,
  };
}
